import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { errorMessages } from './components/validateReason';
import styles from './ExclusionModal.module.scss';
import ExclusionForm from './components/ExclusionForm';
import {
  Button,
  Message,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from 'components/core';
import { Stack } from 'components/layout';
import type { ExclusionReasons } from 'pages/StudyList/StudyListItem/ExclusionReason/ExclusionReason';

type ExclusionModalProps = {
  isOpen: boolean;
  reasons: ExclusionReasons;
  setReasons: (reasons: ExclusionReasons) => void;
  handleClose: () => void;
};

const ExclusionModal = ({
  isOpen,
  reasons,
  setReasons,
  handleClose,
}: ExclusionModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiErrorMessages, setApiErrorMessages] = useState<string[] | null>(
    null
  );
  const errorRef = useRef<HTMLDivElement | null>(null);
  const hasAPIError = Boolean(apiErrorMessages?.length);
  const hasEmptyList = reasons.length === 0;

  const getAPIErrorMessage = (messages: string[]) => {
    if (messages.includes('must be unique')) {
      return errorMessages.duplicatedLabel;
    }

    return errorMessages.generic;
  };

  useEffect(() => {
    if (hasAPIError) {
      errorRef.current?.focus();
    }
  }, [hasAPIError]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      aria-label="Edit exclusion reasons"
    >
      <ModalHeader>Edit exclusion reasons</ModalHeader>
      <ModalBody>
        <Stack spacing={5}>
          <Text variant="neutral">
            During full text review, you will need to select the reason why a
            study should not be included. Edit the list below to suit your
            review. We recommend ordering these in order of importance.
          </Text>

          <ExclusionForm
            title="Exclusion reason"
            label="Add exclusion reason"
            reasons={reasons}
            setReasons={setReasons}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            onApiError={(errors) => {
              setApiErrorMessages(errors);
            }}
          />
        </Stack>

        {(hasAPIError || hasEmptyList) && (
          <div role="alert" ref={errorRef} tabIndex={-1}>
            <Message
              className={classNames({
                [styles.apiErrorMessage]: !hasEmptyList,
              })}
              type="danger"
              variant="prominent"
              isVisible={hasAPIError || hasEmptyList}
              dismissible={false}
            >
              {hasEmptyList && <Text>{errorMessages.emptyList}</Text>}
              {hasAPIError && (
                <Text>{getAPIErrorMessage(apiErrorMessages || [])}</Text>
              )}
            </Message>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="brand" disabled={isSubmitting} onClick={handleClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExclusionModal;
